import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IQueuedReport from "../../../../shared/contracts/IQueuedReport";
import { ICellRendererParams } from "ag-grid-community";
import useReportApi from "../useReportApi";
import { ReportStatus } from "../../../../shared/enums/ReportStatus";
import ConfirmDialog from "../../../../shared/components/confirm-dialog/confirm-dialog.component";
import { useToast } from "../../../../shared/context/toast/toast-context";
import useAppStoreContext from "../../../../shared/context/app-store/app-store.context";

interface MenuCellRendererParams extends ICellRendererParams {
  data: IQueuedReport;
}

const MenuCellRenderer: React.FC<MenuCellRendererParams> = ({ data }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const isOpen = Boolean(anchorEl);
  const { cancelReportSubscription } = useReportApi(); // changed to cancelReportSubscription
  const { showToast } = useToast();
  const { getQueuedReportsList } = useReportApi();
  const { queuedReportsList, setQueuedReportsList } = useAppStoreContext();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: string) => {
    switch (action) {
      case "Cancel From Queue":
        setShowConfirmDialog(true);
        break;
      case "View Status":
        break;
      default:
        console.warn("Unhandled action");
    }
    handleClose();
  };

  const refreshReportList = () => {
    getQueuedReportsList({
      doIf: () => true,
      onFulfilled: (updatedList) => {
        setQueuedReportsList(updatedList);
        showToast("Report list refreshed successfully.");
      },
      onException: (error) => {
        showToast(`Failed to fetch updated list: ${error.message}`, "error");
      },
    });
  };

  const handleCancel = async () => {
    const statusDesc = data.statusDescription?.toUpperCase();
    if (statusDesc === ReportStatus.Queued.toUpperCase()) {
      try {
        await cancelReportSubscription(
          data.reportId,
          data.reportSubscriptionId,
          {
            doBefore: () => {},
            doAfter: () => {},
            doIf: () => true,
            onFulfilled: () => {
              showToast(
                "Report subscription successfully cancelled.",
                "success"
              );
              refreshReportList();
              setShowConfirmDialog(false);
            },
            onException: (error: any) => {
              const errorMessage =
                (error as Error).message || "An unexpected error occurred";
              showToast(`Failed to cancel report: ${errorMessage}`, "error");
              setShowConfirmDialog(false);
            },
          }
        );
      } catch (error: unknown) {
        const errorMsg =
          (error as Error).message || "An unexpected error occurred";
        showToast(`Error occurred during cancellation: ${errorMsg}`, "error");
        setShowConfirmDialog(false);
      }
    } else {
      showToast("Only Queued reports can be cancelled.");
      setShowConfirmDialog(false);
    }
  };

  const isQueued =
    data.statusDescription.toUpperCase() === ReportStatus.Queued.toUpperCase();

  return (
    <>
      <IconButton aria-label="more" aria-haspopup="true" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {isQueued && (
          <MenuItem onClick={() => handleMenuItemClick("Cancel From Queue")}>
            Cancel From Queue
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMenuItemClick("View Status")}>
          View Status
        </MenuItem>
      </Menu>
      {showConfirmDialog && (
        <ConfirmDialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleCancel} // changed onConfirm function
          title="Confirm Cancellation" // changed title
          message="Are you sure you want to cancel this queued report? This action cannot be undone." // changed message
        />
      )}
    </>
  );
};

export default MenuCellRenderer;
